import React from "react";
import { Typography, Box } from "@mui/material";
import { ColumnConfig } from "../../components/DataTable";
import { colors } from "../../theme/colors";

interface SessionDisplay extends Record<string, unknown> {
  id: number;
  provider: {
    id?: number;
    name?: string;
    avatarUrl?: string;
  };
  patient: string;
  patientId: number;
  patientUid: string;
  type: string;
  date: string;
  treatmentPlan?: {
    id: number;
    name: string;
  };
}

interface TableConfigProps {
  formatDate: (dateString: string) => string;
  noPlanAvailableText: string;
  sessionDetailsText: string;
  showProviderColumn: boolean;
  providerColumnTitle: string;
  patientColumnTitle: string;
  typeColumnTitle: string;
  dateColumnTitle: string;
  planColumnTitle: string;
  actionsColumnTitle: string;
  onSessionClick: (sessionId: number) => void;
  onPatientClick: (patientId: number) => void;
  onProviderClick: (providerId: number) => void;
  onTreatmentPlanClick: (planId: number) => void;
}

const ProviderCell: React.FC<{
  name?: string;
  avatarUrl?: string;
  onClick?: () => void;
}> = ({ name, avatarUrl, onClick }) => {
  const displayName = name || "Unknown";

  const getInitials = (str: string) => {
    if (!str) return "?";
    return str
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      {avatarUrl ? (
        <Box
          component="img"
          src={avatarUrl}
          alt={displayName}
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#E8F4F8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: 600,
            color: colors.primary.main,
            flexShrink: 0,
          }}
        >
          {getInitials(displayName)}
        </Box>
      )}
      <Typography className={onClick ? "clickable" : undefined}>
        {displayName}
      </Typography>
    </Box>
  );
};

export const createTableColumns = ({
  formatDate,
  noPlanAvailableText,
  sessionDetailsText,
  showProviderColumn,
  providerColumnTitle,
  patientColumnTitle,
  typeColumnTitle,
  dateColumnTitle,
  planColumnTitle,
  actionsColumnTitle,
  onSessionClick,
  onPatientClick,
  onProviderClick,
  onTreatmentPlanClick,
}: TableConfigProps): ColumnConfig<SessionDisplay>[] => {
  const columns: ColumnConfig<SessionDisplay>[] = [];

  // Conditionally add Provider column
  if (showProviderColumn) {
    columns.push({
      key: "provider",
      title: providerColumnTitle,
      sortable: true,
      render: (value) => {
        const provider = value as {
          id?: number;
          name?: string;
          avatarUrl?: string;
        };
        return (
          <ProviderCell
            name={provider.name}
            avatarUrl={provider.avatarUrl}
            onClick={
              provider.id ? () => onProviderClick(provider.id!) : undefined
            }
          />
        );
      },
    });
  }

  // Add Patient column
  columns.push({
    key: "patient",
    title: patientColumnTitle,
    sortable: true,
    render: (value, row) => (
      <Typography
        className="clickable"
        onClick={() => onPatientClick(row.patientId)}
      >
        {String(value)}
      </Typography>
    ),
  });
  // Add Session Type column
  columns.push({
    key: "type",
    title: typeColumnTitle,
    render: (value) => <Typography>{String(value)}</Typography>,
  });

  // Add Date column
  columns.push({
    key: "date",
    title: dateColumnTitle,
    sortable: true,
    render: (value) => <Typography>{formatDate(String(value))}</Typography>,
  });

  // Add Treatment Plan column
  columns.push({
    key: "treatmentPlan",
    title: planColumnTitle,
    render: (_, row) => {
      const plan = row.treatmentPlan;
      if (!plan) {
        return <Typography>{noPlanAvailableText}</Typography>;
      }
      return (
        <Typography
          className="clickable"
          onClick={() => onTreatmentPlanClick(plan.id)}
        >
          {plan.name}
        </Typography>
      );
    },
  });
  // Add Actions column
  columns.push({
    key: "actions",
    title: actionsColumnTitle,
    render: (_, row) => (
      <Typography
        className="session-details-link"
        onClick={() => onSessionClick(row.id as number)}
      >
        {sessionDetailsText} →
      </Typography>
    ),
  });

  return columns;
};
